const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function formatCurrency(value) {
  const n = Number(value ?? 0);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function sumSalesTotal(sales) {
  return sales.reduce((sum, row) => sum + Number(row.total_amount ?? 0), 0);
}

export function sumExpenseTotals(expenses) {
  return expenses.reduce(
    (sum, expense) => sum + Number(expense.amount ?? 0),
    0,
  );
}

export function sumCustomerTotals(customers) {
  return customers.reduce(
    (acc, c) => {
      acc.debit += Number(c.debit ?? 0);
      acc.credit += Number(c.credit ?? 0);
      acc.balance += Number(c.balance ?? 0);
      return acc;
    },
    { debit: 0, credit: 0, balance: 0 },
  );
}

/** Last N months including current, with sales totals per month */
export function buildSalesByMonth(sales, monthCount = 6) {
  const buckets = [];
  const now = new Date();

  for (let i = monthCount - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    buckets.push({
      key,
      label: `${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`,
      total: 0,
    });
  }

  const indexByKey = Object.fromEntries(buckets.map((b, i) => [b.key, i]));

  sales.forEach((sale) => {
    const raw = sale.sale_date;
    if (!raw) return;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const idx = indexByKey[key];
    if (idx === undefined) return;
    buckets[idx].total += Number(sale.total_amount ?? 0);
  });

  return buckets.map(({ label, total }) => ({ name: label, sales: total }));
}

export function buildCreditDebitChart(customers) {
  const { debit, credit } = sumCustomerTotals(customers);
  return [
    { name: "Credit (CR)", value: credit, fill: "#22c55e" },
    { name: "Debit (DR)", value: debit, fill: "#f59e0b" },
  ].filter((item) => item.value > 0);
}

/** Recent sales count for stat card */
export function countRecentSales(sales, days = 30) {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return sales.filter((s) => {
    const t = new Date(s.sale_date).getTime();
    return !Number.isNaN(t) && t >= cutoff;
  }).length;
}

export function sumStockTotals(stocks) {
  return stocks.reduce(
    (acc, item) => {
      const qty = Number(item.quantity ?? 0);
      const price = Number(item.price ?? 0);
      acc.items += 1;
      acc.quantity += qty;
      acc.value += qty * price;
      if (qty <= 5) acc.lowStock += 1;
      return acc;
    },
    { items: 0, quantity: 0, value: 0, lowStock: 0 },
  );
}

/** Top stock items by quantity for bar chart (max 8) */
export function buildStockQuantityChart(stocks, limit = 8) {
  return [...stocks]
    .sort((a, b) => Number(b.quantity ?? 0) - Number(a.quantity ?? 0))
    .slice(0, limit)
    .map((item) => ({
      name:
        String(item.name || "Item").length > 14
          ? `${String(item.name).slice(0, 12)}…`
          : item.name || "Item",
      fullName: item.name || "Item",
      quantity: Number(item.quantity ?? 0),
      value: Number(item.quantity ?? 0) * Number(item.price ?? 0),
    }));
}

export function getStockListForDashboard(stocks, limit = 6) {
  return [...stocks]
    .sort((a, b) => Number(b.quantity ?? 0) - Number(a.quantity ?? 0))
    .slice(0, limit)
    .map((item) => ({
      id: item.id,
      name: item.name || "—",
      sku: item.sku || "—",
      quantity: Number(item.quantity ?? 0),
      price: Number(item.price ?? 0),
      value: Number(item.quantity ?? 0) * Number(item.price ?? 0),
      isLow: Number(item.quantity ?? 0) <= 5,
    }));
}
